import { Typography } from '@mui/material'
import { styled } from '@mui/system'
import { colorPalette } from '../../../../style/colorPalette'
import { FontSize, FontWeight } from '../../../../../styles/commonStyles'
import { Button } from '../../../../components/buttons'
import { useCallback, useEffect, useState } from 'react'
import { intl } from '../../../../../i18n'
import { useTeamRepository } from '../../../../../services/adaptors/teamRepositoryAdaptor'
import { useProjectPrivateContext } from '../../../../context/projectContext'
import { SelectOption } from '../../../../model/selectOption'
import { WbsItemSearchSearchConditionV2 } from '../../hooks/useSearchConditionV2'
import WbsSearchSearchConditionDialogV2 from './WbsSearchSearchConditionDialogV2'
import { WbsItemSearchSearchItemLabelsV2 } from './WbsItemSearchSearchHeader/WbsItemSearchSearchItemLabelsV2'
import { PermanentUiState } from '../../../../../services/model/uiState'
import {
  SearchConditionAndColumnStateV2,
  SearchConditionAndColumnStateV2StorageService,
} from '../../../../../services/storage-services/ui-states/searchConditionAndColumnStateV2StorageService'
import SaveSearchConditionListDialogV2 from './SaveSearchConditionListDialogV2'
import { WBS_ITEM_SEARCH_COUNT_LIMIT } from '../../wbsItemSearch'
import {
  SHARABLE_UI_STATE_SCOPES,
  UiStateScope,
} from '../../../../../lib/commons/uiStates'
import { useProcessScopeSearchState } from '../../hooks/useProcessScopeSearchState'

const updateUrlArg = (code?: string) => {
  const url = new URL(window.location.href)
  if (code) {
    url.searchParams.set('arg', code)
  } else {
    url.searchParams.delete('arg')
  }
  window.history.replaceState({}, '', url.toString())
}

type Props = {
  count: number
  total: number
  searchCondition: WbsItemSearchSearchConditionV2
  onChange: (patch: Partial<WbsItemSearchSearchConditionV2>) => void
  search: () => void
  resetCondition: () => void
  functionUuid: string
  applyStoredState: (
    state: PermanentUiState<SearchConditionAndColumnStateV2>
  ) => void
  onCloseSearchItemLabel: () => void
  isLoading: boolean
  permanentSearchConditionStorageService: SearchConditionAndColumnStateV2StorageService
}

const WbsItemSearchSearchConditionHeaderV2 = ({
  count,
  total,
  searchCondition,
  search,
  onChange,
  resetCondition,
  functionUuid,
  applyStoredState,
  onCloseSearchItemLabel,
  isLoading,
  permanentSearchConditionStorageService,
}: Props) => {
  const { projectUuid } = useProjectPrivateContext()
  const teamRepository = useTeamRepository()
  const [teamOptions, setTeamOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    if (!projectUuid) return
    const fetchTeams = async () => {
      const teams = await teamRepository.fetchList(projectUuid)
      setTeamOptions(
        teams.map(team => ({
          value: team.uuid,
          name: team.displayName,
          iconUrl: team.iconUrl,
        }))
      )
    }
    fetchTeams()
  }, [projectUuid, teamRepository])

  const [openSearchConditionPanel, setOpenSearchConditionPanel] =
    useState<boolean>(false)
  const [openSavedSearchConditionPanel, setOpenSavedSearchConditionPanel] =
    useState<boolean>(false)

  const onSearch = useCallback(() => {
    search()
    setOpenSearchConditionPanel(false)
  }, [search])

  const handleSearchConditionPanelClose = () => {
    setOpenSearchConditionPanel(false)
    if (count <= WBS_ITEM_SEARCH_COUNT_LIMIT) {
      search()
    }
  }

  const handleSearchConditionPanelOpen = () => {
    setOpenSearchConditionPanel(true)
  }

  const handleSavedSearchConditionPanelClose = () => {
    setOpenSavedSearchConditionPanel(false)
  }

  const handleSavedSearchConditionPanelOpen = () => {
    setOpenSavedSearchConditionPanel(true)
  }

  const onSelectState = useCallback(
    (selected: PermanentUiState<SearchConditionAndColumnStateV2>) => {
      applyStoredState(selected)
      const isSharable = SHARABLE_UI_STATE_SCOPES.includes(
        selected.scope as UiStateScope
      )
      updateUrlArg(isSharable ? selected.code : undefined)
    },
    [applyStoredState]
  )

  const handleChange = useCallback(
    (patch: Partial<WbsItemSearchSearchConditionV2>) => {
      onChange(patch)
      updateUrlArg()
    },
    [onChange]
  )

  const onChangeRootUuids = useCallback(
    (value: string[] | undefined) => handleChange({ rootUuids: value }),
    [handleChange]
  )

  const {
    processOptions,
    processParentMap,
    processOptionsLoading,
    includeDeliverableOptions,
    onToggleIncludeDeliverableOptions,
    isDeliverableSelected,
    isToggleDisabled,
  } = useProcessScopeSearchState(
    projectUuid,
    searchCondition.rootUuids,
    onChangeRootUuids
  )

  const handleResetCondition = useCallback(() => {
    resetCondition()
    updateUrlArg()
  }, [resetCondition])

  const handleCloseSearchItemLabel = useCallback(() => {
    onCloseSearchItemLabel()
    updateUrlArg()
  }, [onCloseSearchItemLabel])

  const [filteringInput, setFilteringInput] = useState<string>('')

  return (
    <div
      style={{
        display: 'flex',
        borderRadius: '4px',
        padding: '10px 10px 0',
        backgroundColor: 'rgba(216, 221, 229, 0.3)',
        flexDirection: 'column',
        height: 'fit-content',
      }}
    >
      <HeaderRow>
        <Typography
          sx={{
            fontSize: FontSize.LARGE,
            fontWeight: 700,
            color: colorPalette.monotone[10],
            marginRight: '20px',
            marginLeft: '10px',
          }}
        >
          {intl.formatMessage({
            id: 'wbsItemSearchFilterPanel.filteringItems.label',
          })}
        </Typography>
        <ResultInfoContainer>
          <Typography
            sx={{
              color: colorPalette.monotone[4],
              fontSize: FontSize.MEDIUM,
              fontWeight: FontWeight.NORMAL,
              marginTop: '1px',
              marginRight: '10px',
            }}
          >
            {intl.formatMessage({
              id: 'wbs.search.header.search.panel.result',
            })}
          </Typography>
          <Typography
            sx={{
              color: colorPalette.monotone[10],
              fontWeight: 700,
              fontSize: FontSize.LARGE,
            }}
          >
            {intl.formatMessage(
              { id: 'wbs.search.header.search.hits' },
              { hit: count }
            )}
          </Typography>
          <Typography
            sx={{
              color: colorPalette.monotone[4],
              fontSize: FontSize.MEDIUM,
              fontWeight: 500,
              margin: '5px 5px 0 5px',
            }}
          >
            /
          </Typography>
          <Typography
            sx={{
              color: colorPalette.monotone[4],
              fontSize: FontSize.MEDIUM,
              fontWeight: 500,
              margin: '5px 0 0 0',
            }}
          >
            {intl.formatMessage(
              { id: 'wbs.search.header.search.total' },
              { total }
            )}
          </Typography>
        </ResultInfoContainer>
        <div>
          <Button
            onClick={handleSearchConditionPanelOpen}
            colorPattern="skyBlue"
            disabled={isLoading}
            sx={{ border: 'none', margin: '0 5px', cursor: 'pointer' }}
          >
            <Typography sx={{ fontSize: '12px' }}>
              {intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.add.condition',
              })}
            </Typography>
          </Button>
          <WbsSearchSearchConditionDialogV2
            count={count}
            total={total}
            open={openSearchConditionPanel}
            onClose={handleSearchConditionPanelClose}
            searchCondition={searchCondition}
            onEnter={search}
            onChangeCondition={handleChange}
            resetCondition={handleResetCondition}
            search={onSearch}
            filteringInput={filteringInput}
            setFilteringInput={setFilteringInput}
            onSelectState={onSelectState}
            functionUuid={functionUuid}
            teamOptions={teamOptions}
            permanentSearchConditionStorageService={
              permanentSearchConditionStorageService
            }
            processOptions={processOptions}
            processParentMap={processParentMap}
            processOptionsLoading={processOptionsLoading}
            includeDeliverableOptions={includeDeliverableOptions}
            onToggleIncludeDeliverableOptions={
              onToggleIncludeDeliverableOptions
            }
            isDeliverableSelected={isDeliverableSelected}
            isToggleDisabled={isToggleDisabled}
          />
        </div>
        <Button
          onClick={handleSavedSearchConditionPanelOpen}
          colorPattern="monotone"
          variant="outlined"
          sx={{ margin: '0 5px', cursor: 'pointer' }}
        >
          <Typography sx={{ fontSize: '12px' }}>
            {intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.saved.condition',
            })}
          </Typography>
        </Button>
        <SaveSearchConditionListDialogV2
          open={openSavedSearchConditionPanel}
          handleSavedSearchConditionPanelClose={
            handleSavedSearchConditionPanelClose
          }
          filteringInput={filteringInput}
          setFilteringInput={setFilteringInput}
          onSelectState={onSelectState}
          functionUuid={functionUuid}
          searchCondition={searchCondition}
          storageService={permanentSearchConditionStorageService}
        />
      </HeaderRow>
      <WbsItemSearchSearchItemLabelsV2
        searchCondition={searchCondition}
        onChange={handleChange}
        search={search}
        onClose={handleCloseSearchItemLabel}
        isLoading={isLoading}
        teamOptions={teamOptions}
        processOptions={processOptions}
        processParentMap={processParentMap}
        processOptionsLoading={processOptionsLoading}
        includeDeliverableOptions={includeDeliverableOptions}
        onToggleIncludeDeliverableOptions={onToggleIncludeDeliverableOptions}
        isDeliverableSelected={isDeliverableSelected}
        isToggleDisabled={isToggleDisabled}
      />
    </div>
  )
}

const HeaderRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
})

const ResultInfoContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginRight: '20px',
  marginLeft: '10px',
})

export default WbsItemSearchSearchConditionHeaderV2
